import React from 'react';
import './HowToReach.css';

function HowToReach() {
    const navaVidhaBhakti = [
        {
            number: 1,
            name: "Sravanam",
            sanskrit: "(Hearing)",
            description: "Hearing about the names, forms, qualities, and pastimes of the Supreme Lord Krishna from authorized scriptures and pure devotees.",
            example: "Maharaja Parikshit - The great king who achieved perfection by hearing Srimad Bhagavatam from Sukadeva Goswami during the last seven days of his life.",
            icon: "👂",
            color: "#e3f2fd"
        },
        {
            number: 2,
            name: "Kirtanam",
            sanskrit: "(Chanting)",
            description: "Chanting and singing the glories of the Lord, especially the Hare Krishna maha-mantra: Hare Krishna Hare Krishna Krishna Krishna Hare Hare, Hare Rama Hare Rama Rama Rama Hare Hare.",
            example: "Sukadeva Goswami - The great sage who attained perfection by constantly chanting and glorifying Lord Krishna's pastimes.",
            icon: "🎵",
            color: "#fff3e0"
        },
        {
            number: 3,
            name: "Smaranam",
            sanskrit: "(Remembering)",
            description: "Constantly remembering the Lord's divine form, names, pastimes, and teachings in all activities throughout the day.",
            example: "Prahlada Maharaja - The devotee prince who remembered Krishna even while being tortured by his demoniac father Hiranyakashipu.",
            icon: "🧘",
            color: "#f3e5f5"
        },
        {
            number: 4,
            name: "Pada-sevanam",
            sanskrit: "(Serving His Feet)",
            description: "Serving the lotus feet of the Lord through temple worship, cleaning His temple, offering flowers, and engaging in various services.",
            example: "Goddess Lakshmi - The goddess of fortune who eternally serves the lotus feet of Lord Narayana in Vaikuntha.",
            icon: "🙏",
            color: "#e8f5e9"
        },
        {
            number: 5,
            name: "Archanam",
            sanskrit: "(Deity Worship)",
            description: "Worshiping the deity form of the Lord with devotion, offering flowers, incense, food, and prayers according to scriptural guidelines.",
            example: "Maharaja Prithu - The ideal king who achieved perfection through regular and elaborate deity worship of Lord Vishnu.",
            icon: "🛕",
            color: "#fce4ec"
        },
        {
            number: 6,
            name: "Vandanam",
            sanskrit: "(Offering Prayers)",
            description: "Offering prayers, prostrations, and respectful obeisances to the Supreme Lord with humility and devotion.",
            example: "Akrura - The devotee who achieved spiritual advancement by constantly offering prayers to Krishna while engaged in material activities.",
            icon: "🤲",
            color: "#fff9c4"
        },
        {
            number: 7,
            name: "Dasyam",
            sanskrit: "(Servitorship)",
            description: "Serving the Lord as His eternal servant, considering oneself as His property and dedicating all actions to His pleasure.",
            example: "Hanuman - The perfect servant of Lord Rama who demonstrated unwavering dedication and service to his master.",
            icon: "🤝",
            color: "#e0f2f1"
        },
        {
            number: 8,
            name: "Sakhyam",
            sanskrit: "(Friendship)",
            description: "Developing a friendly relationship with Krishna, treating Him as one's dearmost friend and confidant.",
            example: "Arjuna - Krishna's intimate friend who received the Bhagavad Gita and fought for righteousness with Krishna's guidance.",
            icon: "👥",
            color: "#ede7f6"
        },
        {
            number: 9,
            name: "Atma-nivedanam",
            sanskrit: "(Complete Surrender)",
            description: "Complete self-surrender to Krishna, offering everything - body, mind, and possessions - for His service without any expectation.",
            example: "Bali Maharaja - The great king who surrendered everything to Lord Vamana, including his body, kingdom, and pride.",
            icon: "💝",
            color: "#ffebee"
        }
    ];

    return (
        <div className="how-to-reach">
            <h1 className="page-title">🛤️ How to Reach Goloka Dham 🛤️</h1>

            <div className="intro-box">
                <h2>The Nine Processes of Bhakti-Yoga</h2>
                <p>
                    According to the great sage Prahlada Maharaja and confirmed in the Srimad Bhagavatam (7.5.23-24),
                    there are nine primary processes of devotional service (Nava Vidha Bhakti). By practicing even
                    one of these processes perfectly, a devotee can attain the supreme perfection of returning to
                    Goloka Dham and engaging in eternal loving service to Lord Krishna.
                </p>
            </div>

            <div className="bhakti-processes">
                {navaVidhaBhakti.map((process) => (
                    <div key={process.number} className="process-card" style={{ borderLeftColor: '#ff6b35' }}>
                        <div className="process-header" style={{ background: process.color }}>
                            <div className="process-number">{process.number}</div>
                            <div className="process-icon">{process.icon}</div>
                            <div className="process-title">
                                <h3>{process.name} {process.sanskrit}</h3>
                            </div>
                        </div>

                        <div className="process-body">
                            <div className="process-description">
                                <h4>Description:</h4>
                                <p>{process.description}</p>
                            </div>

                            <div className="process-example">
                                <h4>🌟 Perfect Example:</h4>
                                <p><strong>{process.example.split(' - ')[0]}</strong> - {process.example.split(' - ')[1]}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="scripture-quote">
                <h3>📖 Scriptural Authority</h3>
                <div className="quote-box">
                    <p className="verse">
                        "śravaṇaṁ kīrtanaṁ viṣṇoḥ smaraṇaṁ pāda-sevanam<br />
                        arcanaṁ vandanaṁ dāsyaṁ sakhyam ātma-nivedanam"
                    </p>
                    <p className="translation">
                        "Hearing, chanting, remembering, serving the lotus feet, worshiping, offering prayers,
                        serving as a servant, befriending, and complete self-surrender - these nine processes
                        constitute pure devotional service."
                    </p>
                    <p className="reference">— Srimad Bhagavatam 7.5.23</p>
                </div>
            </div>

            <div className="important-note">
                <h3>⚡ Important Points to Remember</h3>
                <ul>
                    <li>Even practicing one of these nine processes sincerely can lead to perfection</li>
                    <li>All nine processes are interconnected and naturally flow from one another</li>
                    <li>Chanting the Hare Krishna maha-mantra encompasses all nine processes</li>
                    <li>Association with pure devotees (sadhu-sanga) is essential for progress</li>
                    <li>Avoiding offenses to devotees (Vaisnava Aparadh) is crucial for advancement</li>
                    <li>Regular practice with faith and determination is the key to success</li>
                </ul>
            </div>

            <div className="path-section">
                <h3>🌈 The Progressive Path</h3>
                <div className="path-steps">
                    <div className="path-step">
                        <div className="step-number">1</div>
                        <h4>Adau Shraddha</h4>
                        <p>Initial faith in Krishna and His teachings</p>
                    </div>
                    <div className="path-arrow">→</div>
                    <div className="path-step">
                        <div className="step-number">2</div>
                        <h4>Sadhu Sanga</h4>
                        <p>Association with devotees</p>
                    </div>
                    <div className="path-arrow">→</div>
                    <div className="path-step">
                        <div className="step-number">3</div>
                        <h4>Bhajana Kriya</h4>
                        <p>Beginning devotional practices</p>
                    </div>
                    <div className="path-arrow">→</div>
                    <div className="path-step">
                        <div className="step-number">4</div>
                        <h4>Anartha Nivritti</h4>
                        <p>Cleansing of unwanted habits</p>
                    </div>
                    <div className="path-arrow">→</div>
                    <div className="path-step">
                        <div className="step-number">5</div>
                        <h4>Nishtha</h4>
                        <p>Firm steadiness in practice</p>
                    </div>
                    <div className="path-arrow">→</div>
                    <div className="path-step">
                        <div className="step-number">6</div>
                        <h4>Ruci</h4>
                        <p>Taste for devotional service</p>
                    </div>
                    <div className="path-arrow">→</div>
                    <div className="path-step">
                        <div className="step-number">7</div>
                        <h4>Asakti</h4>
                        <p>Attachment to Krishna</p>
                    </div>
                    <div className="path-arrow">→</div>
                    <div className="path-step">
                        <div className="step-number">8</div>
                        <h4>Bhava</h4>
                        <p>Ecstatic emotions</p>
                    </div>
                    <div className="path-arrow">→</div>
                    <div className="path-step">
                        <div className="step-number">9</div>
                        <h4>Prema</h4>
                        <p>Pure love for Krishna - Goloka Dham!</p>
                    </div>
                </div>
            </div>

            <div className="conclusion-box">
                <h3>🙏 Begin Your Journey Today</h3>
                <p>
                    The path back to Goloka Dham begins with a single step. Start chanting the Hare Krishna
                    maha-mantra, associate with devotees, read sacred scriptures like Bhagavad Gita and
                    Srimad Bhagavatam, and engage in devotional service. With sincerity, patience, and the
                    mercy of guru and Krishna, you will gradually advance on this sublime path and ultimately
                    return to your eternal home in the spiritual world.
                </p>
                <div className="maha-mantra">
                    <p>Hare Krishna Hare Krishna Krishna Krishna Hare Hare</p>
                    <p>Hare Rama Hare Rama Rama Rama Hare Hare</p>
                </div>
            </div>
        </div>
    );
}

export default HowToReach;
